import { Component, Input, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { PayAppSummary } from 'src/app/shared/models';

@Component({
    selector: 'app-pay-app-summary',
    templateUrl: './pay-app-summary.component.html',
    styleUrls: ['./pay-app-summary.component.scss'],
    standalone: false
})
export class PayAppSummaryComponent implements OnInit, OnChanges {
  @Input() payApp: any;
  @Input() summary: PayAppSummary;
  @Input() billingSettings: any;

  // Calculated values for display
  contractSummary = {
    originalContractAmount: 0,
    changeOrdersToDate: 0,
    totalContractAmount: 0,
  };

  earningsToDate = {
    workCompletedToDate: 0,
    materialOnHand: 0,
    mohAdvancedThisPeriod: 0,
    mohDrawdownThisPeriod: 0,
    mohChangeThisPeriod: 0,
    totalEarnings: 0,
  };

  deductions = {
    retainagePercent: 5,
    retainageAmount: 0,
    retainageReleased: 0,
    netRetainage: 0,
    liquidatedDamagesDays: 0,
    liquidatedDamagesRate: 0,
    liquidatedDamagesAmount: 0,
    totalDeductions: 0,
  };

  amountDue = {
    totalEarningsToDate: 0,
    lessTotalDeductions: 0,
    lessPreviousPayments: 0,
    amountDueThisPeriod: 0,
  };

  constructor() {}

  ngOnInit(): void {
    this.calculateValues();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.payApp || changes.summary || changes.billingSettings) {
      this.calculateValues();
    }
  }

  private calculateValues(): void {
    if (!this.payApp && !this.summary) {
      return;
    }

    // Use summary if available, otherwise use payApp data
    const data = this.summary || this.payApp;

    // Contract Summary - prefer calculated values from backend
    this.contractSummary = {
      originalContractAmount: data.calculated?.original_contract_amount || data.original_contract_amount || 0,
      changeOrdersToDate: data.calculated?.total_change_orders || data.total_change_orders || 0,
      totalContractAmount: data.calculated?.total_contract_amount || (data.original_contract_amount || 0) + (data.total_change_orders || 0),
    };

    // Earnings to Date
    this.earningsToDate = {
      workCompletedToDate: data.calculated?.work_completed_to_date || data.work_completed_to_date || 0,
      materialOnHand: data.calculated?.moh_to_date ?? data.calculated?.material_on_hand ?? data.material_on_hand ?? 0,
      mohAdvancedThisPeriod: data.calculated?.moh_advanced_this_period || 0,
      mohDrawdownThisPeriod: data.calculated?.moh_drawdown_this_period || 0,
      mohChangeThisPeriod: data.calculated?.moh_change_this_period || 0,
      totalEarnings: data.calculated?.total_earnings || 0,
    };

    if (!this.earningsToDate.totalEarnings) {
      this.earningsToDate.totalEarnings = this.earningsToDate.workCompletedToDate + this.earningsToDate.materialOnHand;
    }

    // Deductions
    // Use ?? (nullish coalescing) instead of || so that 0% retainage is respected (not treated as falsy)
    const retainagePercent = this.payApp?.retainage_percentage ?? this.billingSettings?.retainage_percentage ?? 5;
    const ldRate = this.payApp?.liquidated_damages_rate || this.billingSettings?.liquidated_damages_per_day || 0;
    const ldDays = this.payApp?.liquidated_damages_days || 0;

    this.deductions = {
      retainagePercent,
      retainageAmount: data.calculated?.retainage_amount || (this.earningsToDate.totalEarnings * retainagePercent / 100),
      retainageReleased: this.payApp?.retainage_released || 0,
      netRetainage: data.calculated?.net_retainage || 0,
      liquidatedDamagesDays: ldDays,
      liquidatedDamagesRate: ldRate,
      liquidatedDamagesAmount: data.calculated?.liquidated_damages_amount || (ldDays * ldRate),
      totalDeductions: data.calculated?.total_deductions || 0,
    };

    if (!this.deductions.netRetainage) {
      this.deductions.netRetainage = this.deductions.retainageAmount - this.deductions.retainageReleased;
    }

    if (!this.deductions.totalDeductions) {
      this.deductions.totalDeductions = this.deductions.netRetainage + this.deductions.liquidatedDamagesAmount;
    }

    // Amount Due - prefer payApp.calculated which has previous_payments computed fresh
    // (summary.calculated may not include previous_payments)
    const calc = this.payApp?.calculated || data.calculated;
    this.amountDue = {
      totalEarningsToDate: this.earningsToDate.totalEarnings,
      lessTotalDeductions: this.deductions.totalDeductions,
      lessPreviousPayments: calc?.previous_payments ?? data.previous_payments ?? 0,
      amountDueThisPeriod: calc?.amount_due_this_period || 0,
    };

    if (!this.amountDue.amountDueThisPeriod) {
      this.amountDue.amountDueThisPeriod =
        this.amountDue.totalEarningsToDate -
        this.amountDue.lessTotalDeductions -
        this.amountDue.lessPreviousPayments;
    }

  }

  get hasMohLedger(): boolean {
    return this.payApp?.calculated?.has_moh_ledger === true || this.payApp?.has_moh_ledger === true;
  }

  formatPeriod(): string {
    if (this.payApp?.start_date && this.payApp?.end_date) {
      const start = new Date(this.payApp.start_date).toLocaleDateString('en-US', { timeZone: 'UTC' });
      const end = new Date(this.payApp.end_date).toLocaleDateString('en-US', { timeZone: 'UTC' });
      return `${start} - ${end}`;
    }
    return '';
  }
}
